import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ChevronDown, Plus, Search as SearchIcon, X } from 'lucide-react';
import { toast } from 'sonner';
import { Chip } from '@/components/ui/Chip';
import { InlineLoader } from '@/components/ui/InlineLoader';
import { booksService } from '@/services/books.service';
import { labelsService } from '@/services/labels.service';
import { ApiError } from '@/services/http';
import { LABEL_TYPE_SINGULAR, LABEL_TYPE_TITLES, type LabelType } from '@/constants/label-types';
import { cn } from '@/lib/cn';
import type { BookLabelsView, BookLabelView, LabelView } from '@/types/api';

/**
 * Maps a `LabelType` to the key it occupies on `BookLabelsView` (and
 * `BookView`). The RPCs emit camelCase plurals while the label catalogue
 * uses the snake_case enum — this table is the one place that gap lives.
 */
export const LABEL_BUCKET_BY_TYPE: Record<LabelType, keyof BookLabelsView> = {
  genre: 'genres',
  topic: 'topics',
  reading_level: 'readingLevels',
  interest_level: 'interestLevels',
  historical_event: 'historicalEvents',
  time_period: 'timePeriods',
  book_series: 'bookSeries',
};

/**
 * AdminBookLabelMultiSelect — one collapsible section in the admin book
 * editor's right sidebar (Labels & Categories card). Each instance owns
 * exactly one label type.
 *
 * Behaviour (per ADMIN_DASHBOARD.md §"Label Management"):
 *   - On mount fetches the full catalogue for this type plus the currently
 *     linked labels (owned by the parent via `labels`).
 *   - Expanding the section reveals a search input + checkbox-style rows.
 *     Toggling a row persists immediately — `PUT /books/:id/labels/:id`
 *     to link, `DELETE` to unlink. No batch save.
 *   - The server returns the refreshed `BookLabelsView` on each mutation;
 *     we forward it to `onLabelsChange` so every other sibling section
 *     can re-render from one canonical payload.
 *   - A "+ New …" row lets the admin create a brand-new label inline; on
 *     success the label is linked immediately to the book.
 */
export interface AdminBookLabelMultiSelectProps {
  bookId: string;
  labelType: LabelType;
  /** Labels currently linked to the book for this type. */
  labels: BookLabelView[];
  /** Called after any mutation with the full refreshed BookLabelsView. */
  onLabelsChange: (labels: BookLabelsView) => void;
  className?: string;
}

export function AdminBookLabelMultiSelect({
  bookId,
  labelType,
  labels,
  onLabelsChange,
  className,
}: AdminBookLabelMultiSelectProps) {
  const [catalog, setCatalog] = useState<LabelView[] | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Tracks the label id currently being toggled so we can disable just
  // that row's button — prevents double-clicks creating/removing the same
  // link repeatedly while the server is mid-flight.
  const [pendingLabelId, setPendingLabelId] = useState<string | null>(null);
  // Inline "+ New …" creator state.
  const [addingNew, setAddingNew] = useState(false);
  const [newAlias, setNewAlias] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await labelsService.list(labelType);
        if (cancelled) return;
        rows.sort((a, b) => a.alias.localeCompare(b.alias));
        setCatalog(rows);
      } catch (err) {
        if (cancelled) return;
        setCatalogError(err instanceof ApiError ? err.message : 'Could not load labels.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [labelType]);

  const selectedIds = useMemo(() => new Set(labels.map((l) => l.id)), [labels]);

  const filteredRows = useMemo(() => {
    if (!catalog) return [];
    if (query.trim() === '') return catalog;
    const needle = query.trim().toLowerCase();
    return catalog.filter((row) => row.alias.toLowerCase().includes(needle));
  }, [catalog, query]);

  const toggleLabel = async (label: LabelView) => {
    if (pendingLabelId) return;
    setPendingLabelId(label.id);
    try {
      const isLinked = selectedIds.has(label.id);
      const next = isLinked
        ? await booksService.removeLabel(bookId, label.id)
        : await booksService.addLabel(bookId, label.id);
      onLabelsChange(next);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update the label.');
    } finally {
      setPendingLabelId(null);
    }
  };

  const handleCreateNew = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const alias = newAlias.trim();
    if (!alias) {
      toast.error('Type a name for the new label.');
      return;
    }
    setCreating(true);
    try {
      const created = await labelsService.create({ alias, labelType });
      // Append to the local catalogue in alpha order so the admin sees it
      // immediately alongside the existing rows, even though the fetch
      // happened before creation.
      setCatalog((prev) =>
        prev ? [...prev, created].sort((a, b) => a.alias.localeCompare(b.alias)) : [created],
      );
      // Link the freshly-created label to the book so the action fully
      // satisfies the admin's expectation ("+ Add Fantasy" should mean
      // "Fantasy now applies to this book").
      const next = await booksService.addLabel(bookId, created.id);
      onLabelsChange(next);
      toast.success(`Created and linked "${alias}".`);
      setNewAlias('');
      setAddingNew(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create the label.');
    } finally {
      setCreating(false);
    }
  };

  const title = LABEL_TYPE_TITLES[labelType];
  const panelId = `admin-label-panel-${labelType}`;

  return (
    <section className={cn('py-3 first:pt-0 last:pb-0', className)}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className="flex w-full items-center justify-between gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40"
      >
        <span className="text-[13px] font-semibold text-or-slate-900">
          {title}
          {labels.length > 0 ? (
            <span className="ml-2 rounded-full bg-or-orange-100 px-2 py-0.5 text-[11px] font-semibold text-or-orange-700">
              {labels.length}
            </span>
          ) : null}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-or-slate-500 transition-transform',
            isOpen ? 'rotate-180' : 'rotate-0',
          )}
          aria-hidden
        />
      </button>

      {/* Always show the currently-linked chips even when collapsed — it's
          the main "what's on this book" signal for the admin skimming the
          sidebar. */}
      {labels.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {labels.map((label) => (
            <Chip
              key={label.id}
              variant={labelType === 'genre' ? 'orange' : 'default'}
              className="gap-1.5"
            >
              {label.name}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void toggleLabel({ id: label.id, alias: label.name, labelType, createdAt: '' });
                }}
                disabled={pendingLabelId === label.id}
                aria-label={`Remove ${label.name}`}
                className="rounded-full p-0.5 text-current hover:bg-black/10 disabled:opacity-60"
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </Chip>
          ))}
        </div>
      ) : null}

      {isOpen ? (
        <div id={panelId} className="mt-3 flex flex-col gap-3">
          {catalogError ? (
            <p className="rounded-lg bg-or-rose-100 px-3 py-2 text-[12px] text-or-rose-700">
              {catalogError}
            </p>
          ) : null}

          <div className="relative">
            <SearchIcon
              className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-or-slate-400"
              aria-hidden
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${title.toLowerCase()}…`}
              className="h-9 w-full rounded-full border border-or-slate-200 bg-white pl-8 pr-3 text-[12px] text-or-slate-900 placeholder:text-or-slate-400 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
            />
          </div>

          {catalog === null ? (
            <InlineLoader label="Loading labels…" />
          ) : filteredRows.length === 0 ? (
            <p className="text-[12px] italic text-or-slate-500">
              {query.trim() === ''
                ? `No ${title.toLowerCase()} yet — use "+ New" below.`
                : 'No matches for that search.'}
            </p>
          ) : (
            <ul className="max-h-60 overflow-y-auto pr-1">
              {filteredRows.map((label) => {
                const checked = selectedIds.has(label.id);
                const busy = pendingLabelId === label.id;
                return (
                  <li key={label.id}>
                    <button
                      type="button"
                      onClick={() => void toggleLabel(label)}
                      disabled={busy}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] transition',
                        checked
                          ? 'bg-or-orange-50 text-or-orange-800'
                          : 'text-or-slate-700 hover:bg-or-slate-50',
                        busy && 'opacity-60',
                      )}
                    >
                      <span className="truncate">{label.alias}</span>
                      <span
                        aria-hidden
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border text-[11px]',
                          checked
                            ? 'border-or-orange-500 bg-or-orange-500 text-white'
                            : 'border-or-slate-300',
                        )}
                      >
                        {checked ? '✓' : ''}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Inline "+ New label" creator. Opens into a small form so the
              admin doesn't need a separate modal for a one-field action. */}
          {addingNew ? (
            <form onSubmit={handleCreateNew} className="flex items-center gap-2">
              <input
                autoFocus
                type="text"
                value={newAlias}
                onChange={(e) => setNewAlias(e.target.value)}
                placeholder={`New ${LABEL_TYPE_SINGULAR[labelType]}`}
                maxLength={120}
                className="h-9 min-w-0 flex-1 rounded-full border border-or-slate-200 bg-white px-3 text-[12px] text-or-slate-900 placeholder:text-or-slate-400 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
              />
              <button
                type="submit"
                disabled={creating}
                className={cn(
                  'inline-flex h-9 items-center rounded-full px-3 text-[12px] font-semibold text-white',
                  'or-gradient-btn disabled:cursor-not-allowed disabled:opacity-60',
                )}
              >
                {creating ? 'Adding…' : 'Add'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAddingNew(false);
                  setNewAlias('');
                }}
                disabled={creating}
                className="text-[12px] font-semibold text-or-slate-500 hover:text-or-slate-700"
              >
                Cancel
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setAddingNew(true)}
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-or-orange-600 hover:text-or-orange-700"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              New {LABEL_TYPE_SINGULAR[labelType]}
            </button>
          )}
        </div>
      ) : null}
    </section>
  );
}
